import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { logError } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const teamColors = {
  commanders: { bg: '#5A1414', accent: '#FFB612' },
  capitals: { bg: '#C8102E', accent: '#041E42' },
  nationals: { bg: '#AB0003', accent: '#14225A' },
  wizards: { bg: '#002B5C', accent: '#E31837' },
  dcunited: { bg: '#000000', accent: '#EF3E42' },
  spirit: { bg: '#C4122E', accent: '#1A1A1A' },
  defenders: { bg: '#C8102E', accent: '#000000' },
  dcsoccer: { bg: '#000000', accent: '#EF3E42' },
};

const teamNames = {
  commanders: 'Commanders',
  capitals: 'Capitals',
  nationals: 'Nationals',
  wizards: 'Wizards',
  dcunited: 'DC United',
  spirit: 'Spirit',
  defenders: 'Defenders',
  dcsoccer: 'DC Soccer',
};

const LatestArticleAlert = () => {
  const [latestArticle, setLatestArticle] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchLatestArticle();
  }, []);

  const fetchLatestArticle = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      const articles = response.data.articles || [];
      if (articles.length > 0) {
        // Check if article is within last 7 days
        const latest = articles[0];
        const articleDate = new Date(latest.created_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        
        if (articleDate >= weekAgo) {
          setLatestArticle(latest);
        }
      }
    } catch (error) {
      logError('Error fetching latest article:', error);
    }
  };

  if (!latestArticle || dismissed) return null;

  const colors = teamColors[latestArticle.team] || teamColors.nationals;
  const teamName = teamNames[latestArticle.team] || 'DC Sports';

  return (
    <div
      data-testid="latest-article-alert"
      className="border-b border-white/10"
      style={{ backgroundColor: colors.bg }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <Link 
            to={`/article/${latestArticle.id}`}
            className="flex items-center gap-3 flex-1 min-w-0 group"
          >
            {/* NEW Badge */}
            <span
              className="flex-shrink-0 px-2.5 py-0.5 text-xs font-bold uppercase tracking-[0.15em] bg-white text-slate-900"
            >
              New
            </span>

            {/* Team Badge */}
            <span 
              className="hidden sm:block flex-shrink-0 px-2 py-1 text-xs font-bold uppercase tracking-wide border border-white/30"
              style={{ backgroundColor: colors.accent, color: 'white' }}
            >
              {teamName}
            </span>

            {/* Article Title */}
            <span className="text-white font-bold truncate group-hover:underline">
              {latestArticle.title}
            </span>

            {/* Read Arrow */}
            <ArrowRight className="w-4 h-4 text-white flex-shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>

          {/* Dismiss Button */}
          <button
            onClick={() => setDismissed(true)}
            className="flex-shrink-0 p-1 text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LatestArticleAlert;
